import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import PageFooter from '@/components/feature/PageFooter';
import PaymentSelectModal from '@/components/feature/PaymentSelectModal';
import { useState } from 'react';

const PRODUCT_2 = {
  id: 1,
  name: '약손콩이 200mlX2병',
  price: 50000,
  originalPrice: 60000,
  discount: 17,
  usdtPrice: 32,
  image: 'https://storage.readdy-site.link/project_files/fab39877-057d-4165-9475-a35374cb4014/e638276f-9e94-4716-bc0b-c598f1919967_YAKSONKONGE_11.jpg?v=2b745acb2616ef0d1c374f92935c8f51',
  category: 'yaksonkong',
};

const PRODUCT_5 = {
  id: 2,
  name: '약손콩이 200mlX5병',
  price: 100000,
  originalPrice: 130000,
  discount: 23,
  usdtPrice: 65,
  image: 'https://storage.readdy-site.link/project_files/fab39877-057d-4165-9475-a35374cb4014/e638276f-9e94-4716-bc0b-c598f1919967_YAKSONKONGE_11.jpg?v=2b745acb2616ef0d1c374f92935c8f51',
  category: 'yaksonkong',
};

export default function YaksonkongPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const variant = searchParams.get('variant') || '2';
  const product = variant === '5' ? PRODUCT_5 : PRODUCT_2;
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [variant]);

  const formatPrice = (p: number) => p.toLocaleString('ko-KR');

  const sections = [
    { label: '제품 소개', id: 'intro' },
    { label: '성분 & 효능', id: 'ingredients' },
    { label: '섭취 방법', id: 'howto' },
    { label: '인증 & 품질', id: 'cert' },
    { label: '리뷰', id: 'reviews' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = sectionRefs.current.findIndex((r) => r === entry.target);
            if (idx !== -1) setActiveSection(idx);
          }
        });
      },
      { threshold: 0.3 }
    );
    sectionRefs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
      <Navbar />

      {/* 상단 히어로 */}
      <section className="pt-20 pb-0 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 pt-10 pb-0">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-emerald-600 transition-colors mb-6 cursor-pointer"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            뒤로가기
          </button>

          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
            {/* 이미지 */}
            <div className="w-full lg:w-[480px] flex-shrink-0">
              <div className="w-full h-[340px] md:h-[480px] rounded-2xl overflow-hidden bg-stone-50">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* 용량 선택 */}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => navigate('/products/yaksonkong?variant=2')}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                    variant === '2'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-emerald-400'
                  }`}
                >
                  2병 세트
                </button>
                <button
                  onClick={() => navigate('/products/yaksonkong?variant=5')}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                    variant === '5'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-emerald-400'
                  }`}
                >
                  5병 세트
                </button>
              </div>
            </div>

            {/* 제품 정보 */}
            <div className="flex-1 pb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-3">
                {variant === '5' ? '베스트' : '인기'} · 약손콩이
              </span>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                {product.name}
              </h1>
              <p className="text-sm text-gray-500 mb-5">
                국내산 약손콩 100% · 무방부제 · 무색소 · 무향료
              </p>

              {/* 별점 */}
              <div className="flex items-center gap-1.5 mb-5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                ))}
                <span className="text-sm text-gray-500 ml-1">4.9 (214개 리뷰)</span>
              </div>

              {/* 가격 */}
              <div className="bg-emerald-50 rounded-xl p-4 mb-5">
                <div className="flex items-end gap-3 mb-1">
                  <span className="text-3xl font-bold text-emerald-700">{formatPrice(product.price)}원</span>
                  <span className="text-base text-gray-400 line-through mb-0.5">{formatPrice(product.originalPrice)}원</span>
                  <span className="text-sm font-bold text-rose-500 mb-0.5">-{product.discount}%</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold">
                    <i className="ri-bit-coin-line w-3 h-3 flex items-center justify-center"></i>
                    USDT ${product.usdtPrice} (ERC-20)
                  </span>
                </div>
              </div>

              {/* 핵심 특징 */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { icon: 'ri-leaf-line', text: '국내산 약손콩 100%' },
                  { icon: 'ri-shield-check-line', text: 'HACCP 인증 시설' },
                  { icon: 'ri-flask-line', text: '무방부제·무색소' },
                  { icon: 'ri-heart-pulse-line', text: '이소플라본 풍부' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 flex-shrink-0">
                      <i className={`${item.icon} text-sm`}></i>
                    </span>
                    {item.text}
                  </div>
                ))}
              </div>

              <button
                onClick={() => setPaymentOpen(true)}
                className="w-full py-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base transition-colors cursor-pointer whitespace-nowrap"
              >
                지금 구매하기
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 탭 네비게이션 */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="flex gap-0 overflow-x-auto">
            {sections.map((s, i) => (
              <button
                key={s.id}
                onClick={() => {
                  sectionRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className={`px-4 md:px-6 py-4 text-sm font-semibold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                  activeSection === i
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-gray-400 hover:text-gray-700'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 space-y-20">

        {/* 1. 제품 소개 */}
        <div ref={(el) => { sectionRefs.current[0] = el; }} id="intro">
          <SectionTitle label="제품 소개" title="약손콩이란?" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            <div className="space-y-5">
              <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                <strong className="text-gray-900">약손콩이</strong>는 국내산 약손콩(서리태·쥐눈이콩)을 원료로 한 프리미엄 건강음료입니다.
                콩의 유익한 성분을 그대로 담아 간편하게 섭취할 수 있도록 만들었습니다.
              </p>
              <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                약손콩은 예로부터 <strong className="text-emerald-700">신장·간 건강</strong>에 좋다고 알려진 검은콩의 일종으로,
                일반 콩보다 이소플라본, 안토시아닌 함량이 월등히 높습니다.
              </p>
              <div className="bg-emerald-50 rounded-xl p-5">
                <p className="text-sm font-bold text-emerald-800 mb-3">약손콩이 특징</p>
                <ul className="space-y-2">
                  {[
                    '국내산 약손콩(서리태·쥐눈이콩) 100% 사용',
                    '무방부제 · 무색소 · 무향료 · 무설탕',
                    '저온 추출 공법으로 영양 성분 최대 보존',
                    '1병(200ml) = 약손콩 약 100g 함유',
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-sm text-emerald-700">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0"></i>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="w-full h-[280px] md:h-[340px] rounded-2xl overflow-hidden">
              <img
                src="https://storage.readdy-site.link/project_files/fab39877-057d-4165-9475-a35374cb4014/e638276f-9e94-4716-bc0b-c598f1919967_YAKSONKONGE_11.jpg?v=2b745acb2616ef0d1c374f92935c8f51"
                alt="약손콩이 제품"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* 왜 약손콩인가 */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              {
                icon: 'ri-seedling-line',
                title: '서리태 & 쥐눈이콩',
                desc: '일반 검은콩보다 이소플라본·안토시아닌 함량이 2~3배 높은 프리미엄 원료',
              },
              {
                icon: 'ri-drop-line',
                title: '저온 추출 공법',
                desc: '열에 약한 이소플라본·안토시아닌을 파괴하지 않는 저온 추출로 영양 최대 보존',
              },
              {
                icon: 'ri-award-line',
                title: '국내 생산 · 엄격 품질관리',
                desc: 'HACCP 인증 시설에서 생산, 매 배치마다 성분 분석 검사 실시',
              },
            ].map((c) => (
              <div key={c.title} className="bg-white border border-gray-100 rounded-2xl p-6">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-4">
                  <i className={`${c.icon} text-lg`}></i>
                </span>
                <h4 className="font-bold text-gray-900 text-sm mb-2">{c.title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 2. 성분 & 효능 */}
        <div ref={(el) => { sectionRefs.current[1] = el; }} id="ingredients">
          <SectionTitle label="성분 & 효능" title="약손콩이 주요 성분과 기대 효과" />

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                name: '이소플라본 (Isoflavone)',
                color: 'emerald',
                icon: 'ri-heart-pulse-line',
                effects: ['여성 호르몬 균형 유지', '갱년기 증상 완화', '골다공증 예방 도움', '항산화 작용'],
                desc: '콩류에 풍부한 식물성 에스트로겐으로, 약손콩에는 일반 콩 대비 2~3배 함유',
              },
              {
                name: '안토시아닌 (Anthocyanin)',
                color: 'violet',
                icon: 'ri-eye-line',
                effects: ['강력한 항산화 효과', '눈 건강 보호', '혈관 건강 개선', '노화 방지'],
                desc: '검은콩 껍질의 보라색 색소 성분으로 강력한 항산화 물질',
              },
              {
                name: '사포닌 (Saponin)',
                color: 'teal',
                icon: 'ri-shield-line',
                effects: ['면역력 강화', '콜레스테롤 저하', '항암 효과 연구 중', '혈당 조절 도움'],
                desc: '콩류 특유의 쓴맛 성분으로 다양한 생리활성 기능 보유',
              },
              {
                name: '식물성 단백질',
                color: 'amber',
                icon: 'ri-body-scan-line',
                effects: ['근육 유지 및 합성', '포만감 지속', '체중 관리 도움', '소화 부담 적음'],
                desc: '동물성 단백질 대비 소화 흡수가 용이하며 콜레스테롤 걱정 없음',
              },
            ].map((item) => (
              <div key={item.name} className="border border-gray-100 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className={`w-10 h-10 flex items-center justify-center rounded-full bg-${item.color}-100 text-${item.color}-600`}>
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">{item.name}</h4>
                    <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.effects.map((e) => (
                    <span key={e} className={`px-2.5 py-1 rounded-full bg-${item.color}-50 text-${item.color}-700 text-xs font-medium`}>
                      {e}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* 영양 성분표 */}
          <div className="mt-8 bg-stone-50 rounded-2xl p-6">
            <h4 className="font-bold text-gray-900 mb-4 text-sm">영양 성분표 (1병 200ml 기준)</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 text-gray-500 font-medium">성분</th>
                    <th className="text-right py-2 text-gray-500 font-medium">함량</th>
                    <th className="text-right py-2 text-gray-500 font-medium">%영양소 기준치</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: '열량', value: '85kcal', pct: '-' },
                    { name: '단백질', value: '6g', pct: '11%' },
                    { name: '지방', value: '2.5g', pct: '5%' },
                    { name: '탄수화물', value: '9g', pct: '3%' },
                    { name: '나트륨', value: '5mg', pct: '0%' },
                    { name: '이소플라본', value: '40mg 이상', pct: '-' },
                    { name: '안토시아닌', value: '15mg 이상', pct: '-' },
                  ].map((row, i) => (
                    <tr key={row.name} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-white'}`}>
                      <td className="py-2.5 text-gray-700">{row.name}</td>
                      <td className="py-2.5 text-right text-gray-700 font-medium">{row.value}</td>
                      <td className="py-2.5 text-right text-emerald-600 font-semibold">{row.pct}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 3. 섭취 방법 */}
        <div ref={(el) => { sectionRefs.current[2] = el; }} id="howto">
          <SectionTitle label="섭취 방법" title="올바른 섭취 가이드" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                step: '01',
                icon: 'ri-time-line',
                title: '섭취 시기',
                desc: '아침 공복 또는 식사 30분 전 섭취 시 흡수율이 가장 높습니다. 취침 전 섭취도 효과적입니다.',
              },
              {
                step: '02',
                icon: 'ri-cup-line',
                title: '섭취 방법',
                desc: '1일 1~2병(200~400ml)을 그대로 마시거나, 두유·스무디에 혼합하여 드셔도 좋습니다.',
              },
              {
                step: '03',
                icon: 'ri-calendar-check-line',
                title: '섭취 기간',
                desc: '최소 3개월 이상 꾸준히 섭취하시면 이소플라본·안토시아닌의 효과를 충분히 경험하실 수 있습니다.',
              },
            ].map((item) => (
              <div key={item.step} className="bg-white border border-gray-100 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl font-black text-emerald-100">{item.step}</span>
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-emerald-600 text-white">
                    <i className={`${item.icon} text-base`}></i>
                  </span>
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-2">{item.title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* 주의사항 */}
          <div className="mt-6 bg-amber-50 border border-amber-100 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-amber-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0"></i>
              <div>
                <p className="text-sm font-bold text-amber-800 mb-2">섭취 시 주의사항</p>
                <ul className="space-y-1">
                  {[
                    '콩 알레르기가 있으신 분은 섭취 전 전문의와 상담하세요.',
                    '개봉 후 냉장 보관하시고 2일 이내에 드세요.',
                    '임산부·수유부·어린이는 섭취 전 전문의와 상담하세요.',
                    '의약품이 아니므로 질병 치료 목적으로 사용하지 마세요.',
                  ].map((t) => (
                    <li key={t} className="text-xs text-amber-700 flex items-start gap-1.5">
                      <span className="mt-1 flex-shrink-0">·</span>{t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 4. 인증 & 품질 */}
        <div ref={(el) => { sectionRefs.current[3] = el; }} id="cert">
          <SectionTitle label="인증 & 품질" title="믿을 수 있는 품질 보증" />
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: 'ri-shield-check-line', title: 'HACCP 인증', desc: '식품안전관리인증기준 적합 시설' },
              { icon: 'ri-leaf-line', title: '유기농 인증', desc: '국내 유기농 인증 원료 사용' },
              { icon: 'ri-microscope-line', title: '성분 분석', desc: '공인 기관 영양 성분 검사 완료' },
              { icon: 'ri-truck-line', title: '당일 포장', desc: '주문 즉시 신선 포장 배송' },
            ].map((c) => (
              <div key={c.title} className="flex flex-col items-center text-center bg-white border border-gray-100 rounded-2xl p-5">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-3">
                  <i className={`${c.icon} text-xl`}></i>
                </span>
                <h4 className="font-bold text-gray-900 text-sm mb-1">{c.title}</h4>
                <p className="text-xs text-gray-400 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>

          {/* 제조 과정 */}
          <div className="mt-8">
            <h4 className="font-bold text-gray-900 mb-5 text-sm">제조 과정</h4>
            <div className="flex flex-col md:flex-row gap-0">
              {[
                { step: '원료 선별', desc: '국내산 약손콩 엄선' },
                { step: '세척 & 불림', desc: '정제수로 깨끗이 세척' },
                { step: '저온 추출', desc: '영양 성분 최대 보존' },
                { step: '여과 & 살균', desc: '불순물 제거 및 살균' },
                { step: '충전 & 포장', desc: 'HACCP 시설 포장' },
              ].map((item, i, arr) => (
                <div key={item.step} className="flex md:flex-col items-center md:items-start flex-1">
                  <div className="flex md:flex-row items-center gap-3 md:gap-2 w-full">
                    <div className="flex flex-col md:flex-row items-center md:items-center gap-0 md:gap-0 flex-1">
                      <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {i + 1}
                      </div>
                      {i < arr.length - 1 && (
                        <div className="w-0.5 h-6 md:w-full md:h-0.5 bg-emerald-200 md:flex-1"></div>
                      )}
                    </div>
                  </div>
                  <div className="ml-3 md:ml-0 md:mt-3">
                    <p className="text-sm font-bold text-gray-900">{item.step}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 5. 리뷰 */}
        <div ref={(el) => { sectionRefs.current[4] = el; }} id="reviews">
          <SectionTitle label="리뷰" title="실제 고객 후기" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { name: '김미영', age: '50대 여성', text: '갱년기 증상으로 힘들었는데 3개월 꾸준히 마시니 확실히 좋아졌어요. 이소플라본 덕분인지 열감도 줄고 수면도 좋아졌습니다. 재구매 중이에요!', rating: 5, product: '2병 세트' },
              { name: '박준호', age: '40대 남성', text: '부모님 건강 선물로 구매했는데 정말 좋아하세요. 고소하고 부드러운 맛이라 매일 챙겨 드신다고 하십니다. 5병 세트로 재구매했어요.', rating: 5, product: '5병 세트' },
              { name: '이수진', age: '30대 여성', text: '직장 생활하면서 건강 챙기기 어려웠는데 간편하게 마실 수 있어서 너무 좋아요. 피부도 좋아지고 피로감도 줄었어요. 강력 추천!', rating: 5, product: '2병 세트' },
              { name: '정하늘', age: '60대 여성', text: '의사 선생님 추천으로 시작했는데 정말 효과가 있네요. 콩 특유의 비린 맛 없이 고소하고 맛있어요. 꾸준히 먹을 예정입니다.', rating: 5, product: '5병 세트' },
            ].map((r) => (
              <div key={r.name} className="bg-white border border-gray-100 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {r.name[0]}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{r.name}</p>
                      <p className="text-xs text-gray-400">{r.age} · {r.product}</p>
                    </div>
                  </div>
                  <div className="flex gap-0.5">
                    {Array.from({ length: r.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-xs w-3 h-3 flex items-center justify-center"></i>
                    ))}
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 하단 CTA */}
        <div className="bg-emerald-600 rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Noto Serif KR, serif' }}>
            지금 바로 약손콩이를 경험해보세요
          </h3>
          <p className="text-emerald-100 text-sm mb-6">국내산 약손콩 100% · 무방부제 · 당일 포장 배송</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => { navigate('/products/yaksonkong?variant=2'); setPaymentOpen(true); }}
              className="px-8 py-3.5 rounded-xl bg-white text-emerald-700 font-bold text-sm hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              2병 세트 구매 · 50,000원
            </button>
            <button
              onClick={() => { navigate('/products/yaksonkong?variant=5'); setPaymentOpen(true); }}
              className="px-8 py-3.5 rounded-xl bg-emerald-800 text-white font-bold text-sm hover:bg-emerald-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              5병 세트 구매 · 100,000원
            </button>
          </div>
        </div>

      </div>

      <PageFooter />

      {paymentOpen && (
        <PaymentSelectModal
          product={{
            id: String(product.id),
            name: product.name,
            price: product.price,
            originalPrice: product.originalPrice,
            image: product.image,
            category: product.category,
            usdtPrice: product.usdtPrice,
          }}
          onClose={() => setPaymentOpen(false)}
        />
      )}
    </div>
  );
}

function SectionTitle({ label, title }: { label: string; title: string }) {
  return (
    <div>
      <span className="inline-block px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 text-xs font-semibold mb-3">
        {label}
      </span>
      <h2 className="text-xl md:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Noto Serif KR, serif' }}>
        {title}
      </h2>
    </div>
  );
}
